import { getLocales, Language } from 'assets/locales/Locale';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { groupBy, keys, sumBy } from 'lodash';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import LineItem from 'models/enums/LineItem';
import LossCodeSelect from 'module/claims/components/lossCode/LossCodeSelect';
import { usePublicCreateClaimProvider } from '../Hooks';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import DataMap from 'assets/components/dataMap/DataMap';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import Icon from 'assets/components/icon/Icon';
import Theme from 'assets/themes/Theme.Common';
import { Fragment } from 'react/jsx-runtime';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { getLineItemLabel } from 'module/claims/helper/LineItem.Label';

const getMoneyValue = (value?: number | null) => {
  return Number((value ?? 0).toFixed(2));
};

const getTaxAmount = (taxRate?: number | null, adjudication?: number | null) => {
  return getMoneyValue((taxRate ?? 0) * (adjudication ?? 0));
};

const getTaxRateFromAmount = (amount?: number | null, adjudication?: number | null) => {
  if (!adjudication) return 0;

  return Number(amount ?? 0) / adjudication;
};

type PublicCreateClaimJobItemTableProps = {
  claimJob: Utils.FormData<Module.Claims.Claim.Submit.ClaimJobForUpdate>;
  claimJobIndex: number;
};

export default function PublicCreateClaimJobItemTable({ claimJob, claimJobIndex }: PublicCreateClaimJobItemTableProps) {
  const { lang, currentLanguage } = getLocales();
  const { form, isEditingTax, setIsEditingTax, updateJobItem, deleteJobItem, addJobItem } =
    usePublicCreateClaimProvider();

  const claimJobItems = claimJob.claimJobItems || [];

  if (!claimJobItems.length || form.data?.claimCreationOption !== ClaimCreatorOptions.DEALERSHIP) {
    return null;
  }

  const groupedItems = groupBy(claimJob.claimJobItems, 'jobItemTypeCode');
  const hasExtendedActions = claimJob?.claimJobItems?.some((it) => !it.isCreatedFromTemplate);

  return (
    <>
      <p>{lang.jobItemsParagraph}.</p>
      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.lossCode}</th>
            <th>{lang.baseCost}</th>
            <th>{lang.quantity}</th>
            <th>{lang.requestedAmount}</th>
            <th>{lang.gstHst}</th>
            <th>{lang.pst}</th>
            <th>{lang.total}</th>
            {hasExtendedActions && <th data-actions>{lang.actions}</th>}
          </tr>
        </thead>
        {keys(groupedItems).map((code, i) => {
          const group = groupedItems[code];

          return (
            <Fragment key={code + '-' + i}>
              <thead>
                <tr
                  style={{
                    backgroundColor: Theme.Color.background,
                  }}
                >
                  <th
                    colSpan={7}
                    style={{
                      padding: `${Theme.Size.XS} ${Theme.Size.S}`,
                    }}
                  >
                    {getLineItemLabel(code).toUpperCase()}
                  </th>
                  {hasExtendedActions && (
                    <th
                      style={{
                        padding: `${Theme.Size.XS} ${Theme.Size.S}`,
                      }}
                    >
                      {code !== LineItem.deductible && (
                        <Button
                          class={ThemeButtonCircle}
                          active
                          media="fas-plus"
                          onClick={() => addJobItem(claimJobIndex, code)}
                          htmlElementProps={{
                            title: `${lang.addNew} ${getLineItemLabel(code)} ${lang.item}`,
                          }}
                        />
                      )}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {!group?.length && (
                  <tr>
                    <td colSpan={7}>
                      <Icon class="fas-inbox" style={{ fontSize: Theme.FontSize.XXL }} />
                      <br />
                      {lang.noInfo}
                    </td>
                  </tr>
                )}
                <DataMap
                  data={group}
                  render={({ data: item, index: itemIndex }) => {
                    const error =
                      item.gstHst > 1
                        ? lang.mustBeEqualOrLessThanAmount.replace(
                            '{amount}',
                            `${lang.requestedAmount}: ${item.adjudication?.toFixed(2)}`
                          )
                        : undefined;

                    return (
                      <tr key={itemIndex}>
                        <td
                          colSpan={
                            code === LineItem.deductible
                              ? 3
                              : code === LineItem.labour
                                ? 3
                                : code === LineItem.other
                                  ? 3
                                  : 1
                          }
                        >
                          {code !== LineItem.deductible && (
                            <>
                              {item.lossCodes?.length === 1 ? (
                                currentLanguage === Language.english ? (
                                  item.lossCodes[0].descnEn
                                ) : (
                                  (item.lossCodes[0].descFr ?? item.lossCodes[0].descnEn)
                                )
                              ) : (
                                <LossCodeSelect
                                  width="140px"
                                  lossCodes={item.lossCodes ?? []}
                                  value={item.lossCode}
                                  error={!item.lossCode && lang.required}
                                  onChange={({ lossCode }) => {
                                    updateJobItem(claimJobIndex, 'lossCode', lossCode, item);
                                  }}
                                />
                              )}
                            </>
                          )}
                        </td>

                        {code === LineItem.part && (
                          <td>
                            <NumberInput
                              name="baseCost"
                              value={getMoneyValue(item.baseCost)}
                              step={0.01}
                              style={{ width: '100%', maxWidth: '100px' }}
                              iconBefore={<Icon class="fas-dollar-sign" />}
                              onChange={({ baseCost }) => {
                                updateJobItem(claimJobIndex, 'baseCost', Number(baseCost ?? 0), item);
                              }}
                            />
                          </td>
                        )}
                        {code === LineItem.part && (
                          <td>
                            <NumberInput
                              name="quantity"
                              value={Number(item.quantity ?? 0)}
                              step={1}
                              style={{ width: '100%', maxWidth: '100px' }}
                              iconBefore={<Icon class="fas-dollar-sign" />}
                              onChange={({ quantity }) => {
                                updateJobItem(claimJobIndex, 'quantity', Number(quantity ?? 0), item);
                              }}
                            />
                          </td>
                        )}
                        <td colSpan={code === LineItem.deductible ? 3 : 1}>
                          {item.jobItemTypeCode === LineItem.part || item.jobItemTypeCode === LineItem.deductible ? (
                            <>${formatNumberWithSeparator(getMoneyValue(item.correction))}</>
                          ) : (
                            <NumberInput
                              name="correction"
                              value={getMoneyValue(item.correction)}
                              step={0.01}
                              iconBefore={<Icon class="fas-dollar-sign" />}
                              style={{ width: '100%', maxWidth: '100px' }}
                              onChange={({ correction }) => {
                                updateJobItem(claimJobIndex, 'correction', Number(correction ?? 0), item);
                              }}
                            />
                          )}
                        </td>
                        {code !== LineItem.deductible && (
                          <td>
                            {!isEditingTax ? (
                              <>${formatNumberWithSeparator(getTaxAmount(item.gstHst, item.adjudication))}</>
                            ) : (
                              <NumberInput
                                name="gstHst"
                                value={getTaxAmount(item.gstHst, item.adjudication)}
                                error={error}
                                step={0.01}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                                style={{ width: '100%', maxWidth: '100px' }}
                                onClick={() => {
                                  setIsEditingTax(true);
                                }}
                                onChange={({ gstHst }) => {
                                  updateJobItem(
                                    claimJobIndex,
                                    'gstHst',
                                    getTaxRateFromAmount(Number(gstHst ?? 0), item.adjudication),
                                    item
                                  );
                                }}
                              />
                            )}
                          </td>
                        )}
                        {code !== LineItem.deductible && (
                          <td>
                            {!isEditingTax ? (
                              <>
                                ${formatNumberWithSeparator(getTaxAmount(item.provincialSalesTax, item.adjudication))}
                              </>
                            ) : (
                              <NumberInput
                                name="provincialSalesTax"
                                value={getTaxAmount(item.provincialSalesTax, item.adjudication)}
                                error={error}
                                step={0.01}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                                style={{ width: '100%', maxWidth: '100px' }}
                                onClick={() => {
                                  setIsEditingTax(true);
                                }}
                                onChange={({ provincialSalesTax }) => {
                                  updateJobItem(
                                    claimJobIndex,
                                    'provincialSalesTax',
                                    getTaxRateFromAmount(Number(provincialSalesTax ?? 0), item.adjudication),
                                    item
                                  );
                                }}
                              />
                            )}
                          </td>
                        )}
                        <td>
                          <strong>${formatNumberWithSeparator(getMoneyValue(item.total).toFixed(2))}</strong>
                        </td>
                        {hasExtendedActions && item.jobItemTypeCode !== LineItem.deductible && (
                          <td style={{ padding: Theme.Size.S }}>
                            {!item.isCreatedFromTemplate && (
                              <Button
                                class={ThemeButtonCircle}
                                media={<Icon class="fas-trash" />}
                                onClick={() => {
                                  deleteJobItem(claimJobIndex, code, itemIndex);
                                }}
                                htmlElementProps={{
                                  title: lang.delete,
                                }}
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  }}
                />
              </tbody>
            </Fragment>
          );
        })}
        <tfoot>
          <tr>
            <td colSpan={6} style={{ textAlign: 'right' }}>
              <strong>{lang.totalAmountWithoutTaxes}:</strong>
            </td>
            <td colSpan={1}>
              <strong>
                $
                {formatNumberWithSeparator(sumBy(ClaimJob.aggregates(claimJob as ClaimJob), (it) => it.ttl).toFixed(2))}
              </strong>
            </td>
          </tr>
          <tr>
            <td colSpan={6} style={{ textAlign: 'right' }}>
              <strong>{lang.totalAmountWithTaxes}:</strong>
            </td>
            <td colSpan={1}>
              <strong>
                $
                {formatNumberWithSeparator(
                  sumBy(ClaimJob.aggregates(claimJob as ClaimJob), (it) => it.total).toFixed(2)
                )}
              </strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </>
  );
}
